/**
 * useAppHistory
 *
 * Tracks in-app navigation history for the current tab and stores it in sessionStorage.
 * This allows safe "back" navigation within the app, ensuring that calls to navigate(-1)
 * only go back to routes inside the app and never to external pages or blank tabs.
 *
 * How it works:
 * - Runs on every route change (via useLocation).
 * - Pushes the current path to the "appHistory" array in sessionStorage,
 *   but only if it's different from the previous entry to avoid duplicates.
 *
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { ROUTE_HISTORY_KEY } from '../AppConstants';

const DEFAULT_MAX_SIZE = 30;

export const useAppHistory = (maxSize: number = DEFAULT_MAX_SIZE) => {
    const location = useLocation();

    useEffect(() => {
        const history: Array<string> = JSON.parse(sessionStorage.getItem(ROUTE_HISTORY_KEY) ?? '[]');

        const currentPath = location.pathname + location.search;

        // Only push if it's different from the last one
        if (history[history.length - 1] !== currentPath) {
            // Maintain max size
            if (history.length >= maxSize) {
                history.shift(); // remove oldest entry
            }

            history.push(currentPath);
            sessionStorage.setItem(ROUTE_HISTORY_KEY, JSON.stringify(history));
        }
    }, [location]);
};
